<template>
  <div class="mb-8">
    <div class="subtitle1">{{ $t("modals.addDevicePosturePolicy.action") }}</div>
    <div v-if="isWindows" class="mt-4">
      <v-checkbox
        v-model="config.blockPortableDevices"
        :label="$t('modals.addDevicePosturePolicy.usbLockdown.blockPortableDevices')"
        :ripple="false"
        :rules="required"
        :hide-details="true"
        :validation-value="noPoliciesSelected"
      >
      </v-checkbox>
      <v-checkbox
        v-model="config.blockMassStorage"
        :label="$t('modals.addDevicePosturePolicy.usbLockdown.blockMassStorage')"
        :ripple="false"
        :rules="required"
        :hide-details="false"
        class="mt-2 mb-2"
        :validation-value="noPoliciesSelected"
      >
      </v-checkbox>
    </div>
    <div v-else class="body2" data-testid="block-mass-storage-text">
      {{ $t("modals.addDevicePosturePolicy.usbLockdown.blockMassStorage") }}
    </div>

    <div class="mt-6">
      <v-expansion-panels flat v-model="allowlistPanel">
        <v-expansion-panel expand-icon="" collapse-icon="">
          <v-expansion-panel-title class="pl-0">
            <template #default="{ expanded }">
              <div class="d-flex justify-space-between w-100">
                <div class="d-flex">
                  <v-icon
                    class="mr-2 cursor-pointer"
                    icon="$dropdown"
                    color="primary"
                    :class="{ rotated: expanded }"
                  />
                  <div>
                    <div class="subtitle1 text-primary" data-testid="usb-devices-count">
                      {{
                        $t("modals.addDevicePosturePolicy.usbLockdown.allowlistedUsbDevices", {
                          count: config.allowedUsbDevices?.length || 0,
                        })
                      }}
                    </div>
                    <div class="caption text-medium-emphasis">
                      {{ $t("modals.addDevicePosturePolicy.usbLockdown.allowlistedDescription") }}
                    </div>
                  </div>
                </div>
                <div class="coro-link" @click.stop="addUsbDevice">
                  + {{ $t("modals.addDevicePosturePolicy.usbLockdown.addUsbDevice") }}
                </div>
              </div>
            </template>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <div v-if="config.allowedUsbDevices?.length" class="mt-4">
              <key-value-input
                v-model:value="config.allowedUsbDevices"
                :key-label="$t('modals.addDevicePosturePolicy.usbLockdown.serialNumber')"
                :value-label="$t('modals.addDevicePosturePolicy.usbLockdown.description')"
                :key-config="keyConfig"
                :value-config="valueConfig"
                :show-add-btn="false"
                always-show-remove-button
              />
            </div>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, toRefs } from "vue";
import { DevicePostureOsType } from "@/constants/devices";
import { useI18n } from "vue-i18n";
import KeyValueInput from "@/components/KeyValueInput.vue";
import type { AllowedUsbDevice } from "@/_store/device-posture/devices-posture-settings.module";

export default defineComponent({
  components: {
    KeyValueInput,
  },
  props: {
    config: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const i18n = useI18n();
    const { config } = toRefs(props);
    const allowlistPanel = ref(0);

    const isWindows = computed(() => {
      return config.value.osType === DevicePostureOsType.WINDOWS;
    });

    const noPoliciesSelected = computed(() => {
      return !config.value.blockPortableDevices && !config.value.blockMassStorage;
    });

    const keyConfig = {
      field: "serialNumber",
      cols: 6,
      md: 6,
      rules: [
        (value: string) => {
          if (!value?.trim()) {
            return i18n.t("validations.required");
          }
          return true;
        },
      ],
    };

    const valueConfig = {
      field: "description",
      cols: 6,
      md: 6,
      rules: [],
    };

    const addUsbDevice = () => {
      if (!config.value.allowedUsbDevices) {
        config.value.allowedUsbDevices = [];
      }
      const newDevice: AllowedUsbDevice = { serialNumber: "", description: "" };
      config.value.allowedUsbDevices.push(newDevice);
      allowlistPanel.value = 0;
    };

    return {
      required: [
        (value: boolean) => {
          if (value) {
            return i18n.t("validations.required");
          }

          return true;
        },
      ],
      isWindows,
      allowlistPanel,
      keyConfig,
      valueConfig,
      addUsbDevice,
      noPoliciesSelected,
    };
  },
});
</script>
